<template>
	<div class="relative isolate overflow-visible w-full max-w-container mx-auto">
		<div class="lg:hidden flex flex-col items-start gap-2 min-h-36 px-4 md:px-10">
			<motion.div
				v-if="!deferAnimations"
				:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
				:initial="{ opacity: 0, transform: 'translateY(10px)' }"
				:transition="{ duration: 1, delay: 0.1 }"
				:in-view-options="{ once: true }"
				class="text-primary text-5xl sm:text-7xl tracking-tight font-semibold will-change-transform mt-2 lg:mt-0"
			>
				{{ $t('home.hero.title') }}
			</motion.div>
			<div
				v-else
				class="text-primary text-5xl sm:text-7xl tracking-tight font-semibold opacity-100 mt-2 lg:mt-0"
			>
				{{ $t('home.hero.title') }}
			</div>

			<Motion
				v-if="!deferAnimations"
				as-child
				:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
				:initial="{ opacity: 0, transform: 'translateY(25px)' }"
				:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.1 }"
				:in-view-options="{ once: true }"
			>
				<UButton
					:label="$t('links.check-availability')"
					:to="localePath('/get-fiber')"
					size="lg"
					class="w-48 uppercase will-change-transform"
					variant="outline"
					color="primary"
				/>
			</Motion>
			<UButton
				v-else
				:label="$t('links.check-availability')"
				:to="localePath('/get-fiber')"
				size="lg"
				class="w-48 uppercase opacity-100"
				variant="outline"
				color="primary"
			/>
		</div>

		<UPageHero
			reverse
			:title="$t('home.hero.title')"
			orientation="horizontal"
			:description="$t('home.hero.description')"
			:ui="{
				container: 'flex flex-col items-center lg:grid py-24 md:py-32 lg:py-8 xl:py-24 gap-16 md:gap-x-0 md:gap-y-24 relative isolate overflow-visible',
				title: 'xl:-mt-24'
			}"
		>
			<template #title>
				<motion.div
					v-if="!deferAnimations"
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(10px)' }"
					:transition="{ duration: 1, delay: 0.1 }"
					:in-view-options="{ once: true }"
					class="hidden lg:block text-primary text-5xl sm:text-7xl tracking-tight font-semibold lg:whitespace-nowrap will-change-transform"
				>
					{{ $t('home.hero.title') }}
				</motion.div>
				<div
					v-else
					class="hidden lg:block text-primary text-5xl sm:text-7xl tracking-tight font-semibold lg:whitespace-nowrap opacity-100"
				>
					{{ $t('home.hero.title') }}
				</div>

				<motion.div
					v-if="!deferAnimations"
					:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
					:initial="{ opacity: 0, transform: 'translateX(50px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.7 }"
					:in-view-options="{ once: true }"
					class="block text-4xl sm:text-6xl text-pretty tracking-tight font-semibold text-highlighted will-change-transform"
				>
					{{ $t('home.hero.subtitle1') }}
				</motion.div>
				<div
					v-else
					class="block text-4xl sm:text-6xl text-pretty tracking-tight font-semibold text-highlighted opacity-100"
				>
					{{ $t('home.hero.subtitle1') }}
				</div>

				<motion.div
					v-if="!deferAnimations"
					:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
					:initial="{ opacity: 0, transform: 'translateX(50px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 1.3 }"
					:in-view-options="{ once: true }"
					class="block text-4xl sm:text-6xl text-pretty tracking-tight font-semibold text-highlighted will-change-transform"
				>
					{{ $t('home.hero.subtitle2') }}
				</motion.div>
				<div
					v-else
					class="block text-4xl text-pretty tracking-tight font-semibold text-highlighted opacity-100"
				>
					{{ $t('home.hero.subtitle2') }}
				</div>
			</template>
			<template #description>
				<motion.div
					v-if="!deferAnimations"
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(10px)' }"
					:transition="{ duration: 0.8, delay: 1.3 }"
					:in-view-options="{ once: true }"
					class="will-change-transform text-highlighted"
				>
					{{ $t('home.hero.description') }}
				</motion.div>
				<div
					v-else
					class="text-highlighted opacity-100"
				>
					{{ $t('home.hero.description') }}
				</div>
			</template>
			<template #links>
				<Motion
					v-if="!deferAnimations"
					as-child
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(25px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.1 }"
					:in-view-options="{ once: true }"
				>
					<SignupAddressChecker
						:tracking-location="trackingLocation"
						compact
						class="will-change-transform"
					/>
				</Motion>
				<SignupAddressChecker
					v-else
					:tracking-location="trackingLocation"
					compact
					class="opacity-100"
				/>
			</template>

			<!-- Keep image static for LCP optimization -->
			<div class="max-w-[208px] sm:max-w-[302px] lg:max-w-[359px] xl:max-w-[417px] w-full lg:flex lg:items-end lg:justify-end -mt-4 lg:ml-auto lg:mt-42 xl:mt-16">
				<SvgImage
					id="svg-image-home-hero"
					provider="storyblok"
					fetchpriority="high"
					sizes="208px sm:302px lg:359px xl:417px"
					preload
					:lazy="false"
					:defer-animations="deferAnimations"
					:image="{
						url: hero?.image?.filename,
						alt: hero?.image?.alt || 'Maverix Fiber-fast internet',
						offsetY: -192,
						offsetX: 0,
						height: 702,
						width: 519
					}"
					:lines="[
						{
							rotation: -15,
							length: 150,
							direction: 'counter-clockwise',
							gap: 0
						}
					]"
					:circles="[
						{
							cx: 88,
							cy: 160,
							r: 120,
							fill: '#01629E'
						},
						{
							cx: 482,
							cy: 176,
							r: 18,
							fill: '#f98c03'
						}
					]"
					:viewbox="{
						width: 534,
						height: 534
					}"
					class="h-full w-full min-h-[273px] sm:min-h-[397px] lg:min-h-[472px] xl:min-h-[548px] pb-6 xl:pb-24 lg:pb-0 mt-0 -mb-3 sm:mt-8 sm:-mb-12 lg:mt-24 xl:mt-16 sm:mr-8 lg:mr-16 xl:mr-6 xl:-mb-24 shrink-0"
				/>
			</div>
		</UPageHero>
	</div>
</template>

<script setup lang="ts">
import type { HeroV2 } from '@/types/storyblok/hero'
import {
	motion, Motion
} from 'motion-v'

const props = defineProps({
	hero: {
		type: Object as PropType<HeroV2>,
		required: true
	},
	trackingLocation: {
		type: String,
		required: true
	},
	deferAnimations: {
		type: Boolean,
		default: false
	}
})

const localePath = useLocalePath()
</script>
